// src/pages/PrivacyPage.js
import React from 'react';
import HeaderBar from '../components/HeaderBar';
import Footer from '../components/Footer';
import '../styles/LandingPage.css';

const sections = [
  {
    title: 'Information We Collect',
    body: (
      <ul>
        <li>
          <strong>Anonymous identifiers:</strong> stable, non-personal IDs created after you accept
          our Anonymous ID policy.
        </li>
        <li>
          <strong>Survey content:</strong> prompts, drafts, published questions, responses, and
          feedback submitted through getSurvey.
        </li>
        <li>
          <strong>Usage data:</strong> timestamps, browser or device metadata, and feature analytics
          that help maintain reliability and security.
        </li>
        <li>
          <strong>Location data:</strong> optional and only collected when you explicitly enable
          geo-targeted surveys.
        </li>
        <li>
          <strong>Integrations:</strong> limited metadata shared with trusted processors (such as
          OpenAI) to support AI-assisted features. We do not share personal data with these partners.
        </li>
      </ul>
    ),
  },
  {
    title: 'How We Use Information',
    body: (
      <ul>
        <li>Operate, improve, and secure getSurvey for participants and creators.</li>
        <li>Generate survey drafts, publish surveys, and deliver shareable links you request.</li>
        <li>Provide analytics and notifications when you choose to receive them.</li>
        <li>Prevent abuse, enforce rate limits, and meet legal obligations.</li>
      </ul>
    ),
  },
  {
    title: 'How We Share Information',
    body: (
      <ul>
        <li>
          <strong>Legal and safety:</strong> shared only if required by law or to protect rights and
          prevent harm.
        </li>
        <li>
          <strong>Collaborators:</strong> teammates or audience members you invite to view or
          respond to surveys.
        </li>
        <li>We never sell or rent personal information.</li>
      </ul>
    ),
  },
  {
    title: 'Cookies and Similar Technologies',
    body: (
      <p>
        Cookies help authenticate sessions, prevent abuse, remember preferences, and understand
        usage patterns. You can control cookies through browser settings. Essential cookies are
        required for core functionality.
      </p>
    ),
  },
  {
    title: 'Data Retention',
    body: (
      <p>
        Survey content and related telemetry are kept while your account remains active or as needed
        to deliver requested features. When you delete surveys, respond anonymously, or close your
        account, we delete or de-identify data subject to legal retention requirements.
      </p>
    ),
  },
  {
    title: 'Your Choices and Rights',
    body: (
      <ul>
        <li>Access, update, or delete surveys and responses within the product.</li>
        <li>Request account deletion or revoke participation via supports@getsurvey.app.</li>
        <li>Adjust location and cookie preferences at any time.</li>
      </ul>
    ),
  },
  {
    title: "Children's Privacy",
    body: (
      <p>
        getSurvey is not directed to children under 13. We do not knowingly collect personal data
        from children. If a child has provided information, contact us to have it deleted.
      </p>
    ),
  },
  {
    title: 'International Users',
    body: (
      <p>
        getSurvey operates from the United States. By using the service, you consent to the transfer
        and processing of your information in the United States and other regions where our
        providers operate under comparable safeguards.
      </p>
    ),
  },
  {
    title: 'AI-Assisted Features',
    body: (
      <p>
        Some survey-drafting and content suggestions are generated using artificial intelligence
        provided by OpenAI. Only the text you enter as prompts or context for generation is sent to
        OpenAI, over secure HTTPS connections. We do not share identifying information, and data
        exchanged with OpenAI is not used to train their models.
      </p>
    ),
  },
  {
    title: 'Policy Updates',
    body: (
      <p>
        We may update this policy to reflect operational, legal, or regulatory changes. Significant
        updates will include an adjusted “Effective date” and may include additional notice.
      </p>
    ),
  },
  {
    title: 'Contact Us',
    body: (
      <p>
        Questions or privacy requests? Email us at{' '}
        <a href="mailto:supports@getsurvey.app" style={{ color: '#2563eb' }}>
          supports@getsurvey.app
        </a>
        .
      </p>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <div className="landing-page">
      <HeaderBar />
      <main style={{ maxWidth: 760, margin: '16px auto', padding: '0 16px', width: '100%' }}>
        <div className="card" style={{ textAlign: 'left', margin: '12px auto' }}>
          <h1 style={{ marginTop: 0 }}>getSurvey Privacy Policy</h1>
          <p style={{ marginBottom: 8, color: '#4b5563' }}>
            <strong>Effective date:</strong> October 23, 2025
          </p>
          <p>
            getSurvey (“we”, “us”, “our”) provides tools that help teams draft, publish, and share
            surveys. We value your privacy. This policy explains what information we collect, how we
            use it, and the choices you have.
          </p>

          {sections.map((section) => (
            <section key={section.title} style={{ marginTop: 28 }}>
              <h2 style={{ marginBottom: 12 }}>{section.title}</h2>
              {section.body}
            </section>
          ))}
        </div>
      </main>
      <Footer betaOnNewLine />
    </div>
  );
}
